import React, {JSX, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    FaAddressCard,
    FaBars,
    FaChevronDown,
    FaChevronUp,
    FaCog,
    FaHeartbeat,
    FaHome,
    FaIdCard,
    FaMountain,
    FaPhoneAlt,
    FaSignInAlt,
    FaSignOutAlt,
    FaUser,
    FaUserFriends,
    FaUserPlus,
} from "react-icons/fa";
import LoginDialog from "./dialogs/LoginDialog";
import ConfirmationDialog from "./dialogs/ConfirmationDialog";
import {NAV, ROUTES} from "../constants/navigation";
import {useAccount} from "../hooks/AccountContext";
import {getCountOfOpenMembershipRequests} from "../api/membershipTourApi";
import {useDbUpdates} from "../hooks/useDbUpdates";
import {useSaveHandler} from "../hooks/SaveHandlerContext";

type NavRoute = {
    nav: string;
    route: string;
};


interface SidebarProps {
    selected: string;
    onLogout: () => void;
    onNavigate: (key: string, path: string) => void;
}

function Sidebar({
                     selected,
                     onLogout,
                     onNavigate
                 }: SidebarProps): JSX.Element {
    const [collapsed, setCollapsed] = useState(() => window.innerWidth < 900);
    const [profileMenuOpen, setProfileMenuOpen] = useState<boolean>(false);
    const [membershipsMenuOpen, setMembershipsMenuOpen] = useState<boolean>(false);
    const [loginOpen, setLoginOpen] = useState<boolean>(false);
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState<boolean>(false);
    const [saveConfirmOpen, setSaveConfirmOpen] = useState<boolean>(false);
    const [navRoute, setNavRoute] = useState<NavRoute | null>(null)

    const [openTourRequestCount, setOpenTourRequestCount] = useState<number>(0);
    const [openGroupRequestCount, setOpenGroupRequestCount] = useState<number>(0);
    const [openRequestCount, setOpenRequestCount] = useState<number>(0);

    // 🔥 Globaler Account-State aus dem Context
    const {
        account,
        contactData: accountContactData,
        medData: accountMedData,
    } = useAccount();

    const { hasUnsavedChanges, resetSaveState } = useSaveHandler();



    const isLoggedIn = !!account;
    const {t} = useTranslation();

    useEffect(() => {
        const handleResize = () => {
            setCollapsed(window.innerWidth < 900)
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    async function loadOpenTourRequests() {
        if (!account) return;
        try {
            const count = await getCountOfOpenMembershipRequests(account.id);
            setOpenTourRequestCount(count);
            setOpenRequestCount(count);
        } catch (e) {
            console.error("Failed to load open requests", e);
        }
    }

    //TODO: Auch fuer Gruppen laden

    useEffect(() => {
        if (account) {
            loadOpenTourRequests();
        }
    }, [account]);

    useDbUpdates(!!account, loadOpenTourRequests);

    const profileSubItems = [
        {key: NAV.ACCOUNT, icon: <FaUser/>, path: ROUTES.ACCOUNT},
        {key: NAV.CONTACT, icon: <FaAddressCard/>, path: ROUTES.CONTACT},
        {key: NAV.MEDICAL, icon: <FaHeartbeat/>, path: ROUTES.MEDICAL},
        {key: NAV.EMERGENCY, icon: <FaPhoneAlt/>, path: ROUTES.EMERGENCY},
    ];

    const membershipSubItems = [
        // {key: NAV.GROUPS, icon: <FaUsers/>, path: ROUTES.GROUPS},
        {key: NAV.TOURS, icon: <FaMountain/>, path: ROUTES.TOURS},
    ];

    const handleProfileItemClick = () => {
        setProfileMenuOpen(!profileMenuOpen);
    };

    const handleMembershipsItemClick = () => {
        setMembershipsMenuOpen(!membershipsMenuOpen);
    };

    const handleLogoutClick = () => {
        setProfileMenuOpen(false);
        setMembershipsMenuOpen(false);
        setLogoutConfirmOpen(true);
    };

    const confirmLogout = () => {
        onLogout();
        setLogoutConfirmOpen(false);
    };

    const handleNavigationClick = (nav: string, route: string) => {
        if (hasUnsavedChanges) {
            setSaveConfirmOpen(true);
            setNavRoute({ nav, route });
        } else {
            onNavigate(nav, route);
        }
    };

    const handleDiscardChanges = () => {
        resetSaveState();     // Dirty-Flag zurücksetzen
        setSaveConfirmOpen(false);

        if (navRoute) {
            onNavigate(navRoute.nav, navRoute.route);
        }

        setNavRoute(null);
    };

    const handleStayOnPage = () => {
        setSaveConfirmOpen(false);
        // NICHT navigieren
        // NICHT resetten
    };


    return (
        <div className={`sidebar ${collapsed ? "collapsed" : ""}`}>
            <button className="toggle-btn" onClick={() => setCollapsed(!collapsed)}>
                <FaBars/>
            </button>

            <div className="menu-scroll-container">
                <ul className="menu-list">

                    {/* Home */}
                    <li
                        className={selected === NAV.HOME ? "active" : ""}
                        onClick={() => handleNavigationClick(NAV.HOME, ROUTES.HOME)}
                    >
                        <span className="menu-icon"><FaHome/></span>
                        <span className="menu-text">{t("menu.home")}</span>
                    </li>

                    {/* Logged-in view */}
                    {isLoggedIn && (
                        <>
                            {/* Mein Profil */}
                            <li
                                className={selected === NAV.PROFILE ? "active" : ""}
                                onClick={handleProfileItemClick}
                            >
                                <span className="menu-icon"><FaIdCard/></span>
                                <span className="menu-text">
                                    {t("menu.profile")}
                                </span>

                                <span className="submenu-arrow">
                                    {profileMenuOpen ? <FaChevronUp/> : <FaChevronDown/>}
                                </span>

                            </li>

                            {/* Untermenü */}
                            {profileMenuOpen && (
                                <>
                                    {profileSubItems.map((item) => (
                                        <li
                                            key={item.key}
                                            className={`sub-item ${selected === item.key ? "active" : ""}`}
                                            onClick={() => handleNavigationClick(item.key, item.path)}
                                        >
                                            <span className="menu-icon">{item.icon}</span>
                                            <span className="menu-text">{t(`menu.${item.key}`)}</span>
                                        </li>
                                    ))}
                                </>
                            )}

                            {/* Meine Mitgliedschaften */}
                            <li
                                className={selected === NAV.MEMBERSHIPS ? "active" : ""}
                                onClick={handleMembershipsItemClick}
                            >
                                <span className="menu-icon icon-with-badge">
                                    <FaUserFriends/>
                                    {!membershipsMenuOpen && openRequestCount > 0 && (
                                        <span className="sidebar-badge">{openRequestCount}</span>
                                    )}
                                </span>
                                <span className="menu-text">
                                    {t("menu.memberships")}
                                </span>

                                <span className="submenu-arrow">
                                    {membershipsMenuOpen ? <FaChevronUp/> : <FaChevronDown/>}
                                </span>
                            </li>

                            {/* Untermenü */}
                            {membershipsMenuOpen && (
                                <>
                                    {membershipSubItems.map((item) => (
                                        <li
                                            key={item.key}
                                            className={`sub-item ${selected === item.key ? "active" : ""}`}
                                            onClick={() => handleNavigationClick(item.key, item.path)}
                                        >
                                            <span className="menu-icon icon-with-badge">
                                                {item.icon}
                                                {item.key === NAV.TOURS && openTourRequestCount > 0 && (
                                                    <span className="sidebar-badge">{openTourRequestCount}</span>
                                                )}
                                            </span>
                                            <span className="menu-text">{t(`menu.${item.key}`)}</span>
                                        </li>
                                    ))}
                                </>
                            )}
                        </>
                    )}

                    {/* Externe Personendaten anzeigen */}
                    <li
                        className={selected === NAV.SHOW_PERSON ? "active" : ""}
                        onClick={() => handleNavigationClick(NAV.SHOW_PERSON, ROUTES.SHOW_PERSON)}
                    >
                        <span className="menu-icon"><FaUserPlus/></span>
                        <span className="menu-text">{t("menu.showPerson")}</span>
                    </li>
                </ul>
            </div>

            <div className="sidebar-bottom">
                {isLoggedIn ? (
                    <li
                        className={`bottom-item ${selected === "logout" ? "active" : ""}`}
                        onClick={handleLogoutClick}
                    >
                        <span className="menu-icon"><FaSignOutAlt/></span>
                        <span className="menu-text">{t("menu.logout")}</span>
                    </li>
                ) : (
                    <li
                        className={`bottom-item ${selected === "login" ? "active" : ""}`}
                        onClick={() => setLoginOpen(true)}
                    >
                        <span className="menu-icon"><FaSignInAlt/></span>
                        <span className="menu-text">{t("menu.login")}</span>
                    </li>
                )}

                <li
                    className={`bottom-item ${selected === NAV.SETTINGS ? "active" : ""}`}
                    onClick={() => handleNavigationClick(NAV.SETTINGS, ROUTES.SETTINGS)}
                >
                    <span className="menu-icon"><FaCog/></span>
                    <span className="menu-text">{t("menu.settings")}</span>
                </li>
            </div>

            {/* LoginDialog */}
            <LoginDialog
                open={loginOpen}
                onClose={() => setLoginOpen(false)}
            />

            {/* Logout Confirmation */}
            <ConfirmationDialog
                open={logoutConfirmOpen}
                title={t("menu.logout")}
                message={t("menu.logoutConfirm")}
                confirmLabel={t("common.button.logout")}
                cancelLabel={t("common.button.cancel")}
                onConfirm={confirmLogout}
                onClose={() => setLogoutConfirmOpen(false)}
            />

            <ConfirmationDialog
                open={saveConfirmOpen}
                title={t("common.validation.saveChangesTitle")}
                message={t("common.validation.saveChangesMessage")}
                confirmLabel={t("common.validation.stayOnPage")}
                cancelLabel={t("common.validation.discardChanges")}
                onConfirm={handleStayOnPage}     // Speichern → NICHT schließen
                onCancel={handleDiscardChanges}   // Verwerfen → schließen + navigieren
                onClose={handleStayOnPage}      // X / ESC → schließen, NICHT navigieren

                closeOnConfirm={false}            // ← wichtig!
                closeOnCancel={true}              // ← normal
            />
        </div>
    );
}

export default Sidebar;